import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import Button from '../UI/Button';
import Modal from '../UI/Modal';

const schema = yup.object().shape({
    name: yup.string().required('Название смены обязательно'),
    startTime: yup.string().required('Время начала обязательно'),
    endTime: yup.string().required('Время окончания обязательно'),
    breakStart: yup.string().required('Время начала перерыва обязательно'),
    breakEnd: yup.string().required('Время окончания перерыва обязательно'),
    daysOfWeek: yup.array().of(yup.number()).required('Дни недели обязательны')
});

const ShiftScheduleForm = () => {
    const [shifts, setShifts] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingShift, setEditingShift] = useState(null);
    const { user, logout, hasPermission } = useUser();

	const canEdit = hasPermission("shifts", "edit");
	const canDelete = hasPermission("shifts", "delete");
	const canView = hasPermission("shifts", "view");
	const canCreate = hasPermission("shifts", "create");

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm({
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        fetchShifts();
    }, []);

    const fetchShifts = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/shift-schedules`,
                {},
                logout
            );
            const data = await response.json();
            setShifts(data);
        } catch (error) {
            console.error('Ошибка при получении смен:', error);
        }
    };

    const onSubmit = async (data) => {
        try {
            const url = editingShift 
                ? `${process.env.REACT_APP_API_URL}/admin/shift-schedules/${editingShift._id}`
                : `${process.env.REACT_APP_API_URL}/admin/shift-schedules`;
            
            const response = await fetchWithAuth(
                url,
                {
                    method: editingShift ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(data),
                },
                logout
            );

            if (response.ok) {
                fetchShifts();
                setIsModalOpen(false);
                reset();
                setEditingShift(null);
            }
        } catch (error) {
            console.error('Ошибка при сохранении смены:', error);
        }
    };

    const handleEdit = (shift) => {
        setEditingShift(shift);
        setValue('name', shift.name);
        setValue('startTime', shift.startTime);
        setValue('endTime', shift.endTime);
        setValue('breakStart', shift.breakStart);
        setValue('breakEnd', shift.breakEnd);
        setValue('daysOfWeek', shift.daysOfWeek);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить смену?')) return;
        
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/admin/shift-schedules/${id}`,
                {
                    method: 'DELETE',
                },
                logout
            );

            if (response.ok) {
                fetchShifts();
            }
        } catch (error) {
            console.error('Ошибка при удалении смены:', error);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Управление сменами</h2>
                {canCreate && <Button onClick={() => setIsModalOpen(true)} variant="primary">
                    Добавить смену
                </Button>}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full table-auto border border-[#3E3E42]">
                    <thead>
                        <tr className="bg-[#252526]">
                            <th className="p-2 border border-[#3E3E42]">Название</th>
                            <th className="p-2 border border-[#3E3E42]">Начало</th>
                            <th className="p-2 border border-[#3E3E42]">Окончание</th>
                            <th className="p-2 border border-[#3E3E42]">Перерыв</th>
                            <th className="p-2 border border-[#3E3E42]">Дни недели</th>
                            <th className="p-2 border border-[#3E3E42]">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shifts.map((shift) => (
                            <tr key={shift._id} className="text-sm">
                                <td className="p-2 border border-[#3E3E42]">{shift.name}</td>
                                <td className="p-2 border border-[#3E3E42]">{shift.startTime}</td>
                                <td className="p-2 border border-[#3E3E42]">{shift.endTime}</td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {shift.breakStart && shift.breakEnd 
                                        ? `${shift.breakStart} - ${shift.breakEnd}`
                                        : 'Нет'}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    {shift.daysOfWeek.map(day => ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][day-1]).join(', ')}
                                </td>
                                <td className="p-2 border border-[#3E3E42]">
                                    <div className="flex gap-2">
                                        {canEdit && <Button onClick={() => handleEdit(shift)} variant="edit">
                                            Ред.
                                        </Button>}
                                        {canDelete && <Button onClick={() => handleDelete(shift._id)} variant="danger">
                                            Удалить
                                        </Button>}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    reset();
                    setEditingShift(null);
                }}
                title={editingShift ? 'Редактировать смену' : 'Добавить смену'}
            >
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Название смены</label>
                        <input
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...register('name')}
                        />
                        {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Время начала</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...register('startTime')}
                        />
                        {errors.startTime && <p className="text-red-500 text-sm">{errors.startTime.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Время окончания</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...register('endTime')}
                        />
                        {errors.endTime && <p className="text-red-500 text-sm">{errors.endTime.message}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Начало перерыва</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...register('breakStart')}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Конец перерыва</label>
                        <input
                            type="time"
                            className="w-full p-2 rounded bg-[#1E1E1E] border border-[#3E3E42]"
                            {...register('breakEnd')}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Дни недели</label>
                        <div className="grid grid-cols-7 gap-2">
                            {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                                <label key={day} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        value={day}
                                        {...register('daysOfWeek')}
                                        className="mr-2"
                                    />
                                    {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][day-1]}
                                </label>
                            ))}
                        </div>
                        {errors.daysOfWeek && <p className="text-red-500 text-sm">{errors.daysOfWeek.message}</p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => {
                                setIsModalOpen(false);
                                reset();
                                setEditingShift(null);
                            }}
                            variant="cancel"
                        >
                            Отмена
                        </Button>
                        <Button type="submit" variant="primary">
                            {editingShift ? 'Сохранить' : 'Добавить'}
                        </Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ShiftScheduleForm; 